/**
 * CRM journey stage form content — aligned with MD/Compliance/Compliance.md Annex C Part 1.
 * Demo values are deterministic from residentId + residentName for repeatable demos.
 */

const hash = (id) => {
  const n = Number(id) || 1;
  return ((n * 7919) % 100) / 100;
};

/**
 * @param {number} stage 1–9
 * @param {string} [subStage7] '7A' | '7B' | '7C' | '7D' | '7E' — only when stage === 7
 */
export function getJourneyStageFormModel(stage, residentName, residentId, subStage7 = '7B') {
  const name = residentName || 'Resident';
  const id = Number(residentId) || 1;
  const h = hash(id);

  const models = {
    1: {
      title: 'Stage 1 — Enquiry (lead capture)',
      subtitle: 'Inbound contact logged; acknowledgement and follow-up tasks generated.',
      status: 'completed',
      specRef: 'Compliance.md §1.2 Stage 1',
      sections: [
        {
          title: 'Enquiry record',
          fields: [
            { key: 'enquiry_source', label: 'Enquiry source', value: 'My Aged Care referral', hint: 'Enum: MAC, Hospital, GP, Family, Website…' },
            { key: 'enquiry_type', label: 'Enquiry type', value: 'Permanent residential care', hint: '' },
            { key: 'prospective_resident_name', label: 'Prospective resident', value: name, hint: '' },
            { key: 'contact_person_name', label: 'Contact person', value: 'Sarah Thompson', hint: 'Usually family or social worker' },
            { key: 'contact_person_relationship', label: 'Relationship', value: 'Daughter', hint: '' },
            { key: 'contact_phone', label: 'Contact phone', value: '0412 345 678', hint: 'E.164 in production' },
            { key: 'urgency', label: 'Urgency', value: h > 0.85 ? 'Emergency (48h)' : 'Standard', hint: '' },
            { key: 'acat_done', label: 'ACAT / MAC assessment completed', value: 'Yes', hint: 'Boolean — triggers tasks if No' },
            { key: 'mac_referral', label: 'My Aged Care referral code', value: `MAC-${100000 + id * 17}`, hint: 'Conditional' },
            { key: 'notes', label: 'Notes', value: 'Family requested tour within 2 weeks; prefers single room if available.', hint: 'Free text', fullWidth: true }
          ]
        },
        {
          title: 'Routing',
          fields: [
            { key: 'assigned_to', label: 'Assigned to', value: 'Admissions — J. Mitchell', hint: 'FK Staff' },
            { key: 'enquiry_date', label: 'Enquiry date / time', value: '2025-12-10 09:42', hint: 'System timestamp' }
          ]
        }
      ]
    },
    2: {
      title: 'Stage 2 — Qualified lead',
      subtitle: 'Suitability confirmed; ACAT and funding pathway captured.',
      status: 'completed',
      specRef: 'Compliance.md §1.2 Stage 2',
      sections: [
        {
          title: 'Qualification',
          fields: [
            { key: 'qualification_date', label: 'Qualification date', value: '2025-12-15', hint: '' },
            { key: 'acat_approval_date', label: 'ACAT approval date', value: '2025-12-01', hint: 'Required' },
            { key: 'acat_approval_type', label: 'ACAT approval type', value: 'Permanent', hint: '' },
            { key: 'means_testing_status', label: 'Means testing status', value: h > 0.5 ? 'Completed' : 'Lodged', hint: '' },
            { key: 'expected_timeframe', label: 'Expected admission timeframe', value: '1–2 weeks', hint: '' },
            { key: 'lead_score', label: 'Lead score (calculated)', value: `${Math.min(98, 55 + Math.round(h * 40))} — Hot`, hint: '0–100; Hot 75+' }
          ]
        },
        {
          title: 'Clinical snapshot (suitability)',
          fields: [
            { key: 'mobility', label: 'Mobility level', value: 'Walker for distances > 20m', hint: 'Room/wing matching' },
            { key: 'dementia', label: 'Dementia diagnosis', value: 'Yes — Alzheimer\'s', hint: 'Wing assignment' },
            { key: 'behavioural', label: 'Behavioural concerns', value: 'None flagged at qualification', hint: 'Free text' }
          ]
        }
      ]
    },
    3: {
      title: 'Stage 3 — Tour & assessment',
      subtitle: 'Tour conducted; pre-admission clinical suitability recorded.',
      status: 'completed',
      specRef: 'Compliance.md §1.2 Stage 3',
      sections: [
        {
          title: 'Tour',
          fields: [
            { key: 'tour_date', label: 'Tour date / time', value: '2025-12-20 14:00', hint: '' },
            { key: 'tour_conducted_by', label: 'Conducted by', value: 'J. Mitchell (Admissions)', hint: 'FK Staff' },
            { key: 'tour_attendees', label: 'Attendees', value: `${name}, daughter (Sarah Thompson)`, hint: '' },
            { key: 'tour_outcome', label: 'Tour outcome', value: 'Positive — proceeding', hint: 'Enum' },
            { key: 'tour_notes', label: 'Tour notes', value: 'Family happy with memory support unit; asked about GP liaison.', hint: '', fullWidth: true }
          ]
        },
        {
          title: 'Pre-admission assessment',
          fields: [
            { key: 'pre_adm_done', label: 'Pre-admission assessment completed', value: 'Yes', hint: '' },
            { key: 'pre_adm_outcome', label: 'Outcome', value: 'Suitable with conditions', hint: 'Enum' },
            { key: 'clinical_risks', label: 'Clinical risks identified', value: 'Falls risk — moderate; nutrition review scheduled.', hint: 'Conditional', fullWidth: true }
          ]
        }
      ]
    },
    4: {
      title: 'Stage 4 — Waitlist',
      subtitle: 'Intent to proceed; priority and contact rhythm set.',
      status: 'completed',
      specRef: 'Compliance.md §1.2 Stage 4',
      sections: [
        {
          title: 'Waitlist',
          fields: [
            { key: 'waitlist_date', label: 'Waitlist date', value: '2025-12-22', hint: 'System' },
            { key: 'position', label: 'Waitlist position', value: String(3 + (id % 4)), hint: 'Auto-calculated' },
            { key: 'priority', label: 'Priority', value: 'Standard', hint: 'Standard | Priority | Emergency' },
            { key: 'room_type', label: 'Preferred room type', value: 'Single', hint: '' },
            { key: 'status', label: 'Waitlist status', value: 'Converted', hint: 'Active | On hold | Expired | …' },
            { key: 'last_contact', label: 'Last contact date', value: '2026-01-08', hint: '' },
            { key: 'contact_frequency', label: 'Contact frequency', value: 'Fortnightly', hint: 'System' }
          ]
        }
      ]
    },
    5: {
      title: 'Stage 5 — Offer / agreement',
      subtitle: 'Bed offered; quotes and agreements tracked.',
      status: 'completed',
      specRef: 'Compliance.md §1.2 Stage 5',
      sections: [
        {
          title: 'Offer',
          fields: [
            { key: 'offer_date', label: 'Offer date', value: '2026-01-05', hint: '' },
            { key: 'offered_room', label: 'Offered room', value: `Room ${String.fromCharCode(65 + (id % 3))}${100 + id}`, hint: 'FK Room' },
            { key: 'quote_generated', label: 'Quote generated', value: 'Yes', hint: 'System' },
            { key: 'agreement_sent', label: 'Agreement pack sent', value: 'Yes', hint: '' },
            { key: 'agreement_signed', label: 'Agreement signed', value: 'Yes', hint: '' },
            { key: 'agreement_signed_date', label: 'Signed date', value: '2026-01-12', hint: '' },
            { key: 'e_signature', label: 'E-signature used', value: 'Yes', hint: '' },
            { key: 'offer_status', label: 'Offer status', value: 'Accepted', hint: 'Enum' }
          ]
        }
      ]
    },
    6: {
      title: 'Stage 6 — Admission in progress',
      subtitle: 'Post-signature checklist; ACER countdown and clinical onboarding.',
      status: 'completed',
      specRef: 'Compliance.md §1.2 Stage 6',
      sections: [
        {
          title: 'Key tasks (excerpt)',
          fields: [
            { key: 'admission_date', label: 'Confirmed admission date', value: '2026-01-15', hint: '' },
            { key: 'profile_created', label: 'Resident profile created', value: 'Yes — CRM → profile conversion', hint: '' },
            { key: 'room_nfc', label: 'Room / NFC tag', value: 'Assigned & tested', hint: 'IT / Maintenance' },
            { key: 'acer_deadline', label: 'ACER lodgement due', value: '2026-02-12 (28 days)', hint: 'Hard regulatory deadline' },
            { key: 'mar_prepared', label: 'Medication chart prepared', value: 'RN sign-off Day 1', hint: '' },
            { key: 'welcome_pack', label: 'Welcome pack provided', value: 'Yes', hint: 'Day of admission' }
          ]
        }
      ]
    },
    7: null, // handled below by sub-stage
    8: {
      title: 'Stage 8 — Exit / discharge',
      subtitle: 'Not started — shown as template when resident is active.',
      status: 'pending',
      specRef: 'Compliance.md §1.2 Stage 8',
      sections: [
        {
          title: 'Exit workflow (preview)',
          fields: [
            { key: 'status_update', label: 'Resident status update', value: '—', hint: 'Same day' },
            { key: 'services_australia', label: 'Notify Services Australia', value: '—', hint: 'Within 14 days' },
            { key: 'rad_refund', label: 'RAD refund calculation', value: '—', hint: 'Within 14 days' },
            { key: 'nfc', label: 'NFC / QR de-assigned', value: '—', hint: 'Same day' },
            { key: 'records', label: 'Clinical records archived', value: '—', hint: '7-year retention' }
          ]
        }
      ]
    },
    9: {
      title: 'Stage 9 — Post-exit follow-up',
      subtitle: 'Scheduled from 14 days after exit — satisfaction and referral nurture.',
      status: 'pending',
      specRef: 'Compliance.md §1.2 Stage 9',
      sections: [
        {
          title: 'Activities (preview)',
          fields: [
            { key: 'survey', label: 'Satisfaction survey', value: '—', hint: 'Post-exit' },
            { key: 'feedback', label: 'Feedback logged for QI', value: '—', hint: '' },
            { key: 'referral', label: 'Referral source relationship', value: '—', hint: '' }
          ]
        }
      ]
    }
  };

  if (stage === 7) {
    return attachHistory(getStage7SubStageModel(subStage7, name, id, h), 7, id, name, subStage7);
  }

  const base = models[stage] || models[1];
  return attachHistory(base, stage, id, name, subStage7);
}

/**
 * Assessment / visit / contact history rows for the active stage (demo data).
 */
function attachHistory(model, stage, id, name, subStage7) {
  const h = getHistoryForStage(stage, id, name, subStage7);
  if (!h) return model;
  return { ...model, history: h };
}

export function getHistoryForStage(stage, residentId, residentName, subStage7 = '7B') {
  const id = Number(residentId) || 1;
  const name = residentName || 'Resident';
  const h = hash(id);

  const tables = {
    1: {
      title: 'Enquiry & contact history',
      subtitle: 'Calls, emails, and walk-ins for this lead',
      columns: [
        { key: 'when', label: 'Date / time' },
        { key: 'channel', label: 'Channel' },
        { key: 'summary', label: 'Summary' },
        { key: 'staff', label: 'Staff' }
      ],
      rows: [
        { when: '2025-12-10 09:42', channel: 'Phone', summary: 'Initial MAC referral — family requested brochure', staff: 'J. Mitchell' },
        { when: '2025-12-11 14:05', channel: 'Email', summary: 'Sent fee schedule + Statement of Rights pack', staff: 'System' },
        { when: '2025-12-12 10:30', channel: 'Callback', summary: 'Discussed room types; booked tour', staff: 'J. Mitchell' }
      ]
    },
    2: {
      title: 'Qualification & ACAT history',
      subtitle: 'Suitability checks and funding updates',
      columns: [
        { key: 'when', label: 'Date' },
        { key: 'event', label: 'Event' },
        { key: 'outcome', label: 'Outcome' }
      ],
      rows: [
        { when: '2025-12-14', event: 'ACAT approval verified', outcome: 'Permanent — MAC letter uploaded' },
        { when: '2025-12-15', event: 'Clinical suitability review', outcome: 'Proceed — memory support unit' },
        { when: '2025-12-16', event: 'Means testing status', outcome: h > 0.5 ? 'Completed' : 'Lodged — pending' }
      ]
    },
    3: {
      title: 'Tours & pre-admission visits',
      subtitle: 'Multiple visits and assessments before admission',
      columns: [
        { key: 'when', label: 'Date / time' },
        { key: 'type', label: 'Type' },
        { key: 'attendees', label: 'Attendees' },
        { key: 'outcome', label: 'Outcome' },
        { key: 'staff', label: 'Staff' }
      ],
      rows: [
        { when: '2025-12-18 11:00', type: 'Facility tour', attendees: `${name}, daughter`, outcome: 'Positive — follow-up call', staff: 'J. Mitchell' },
        { when: '2025-12-20 14:00', type: 'Second tour + RN meet', attendees: `${name}, Sarah T.`, outcome: 'Positive — proceeding', staff: 'J. Mitchell / RN Chen' },
        { when: '2025-12-21 09:00', type: 'Pre-admission clinical', attendees: 'RN + family', outcome: 'Suitable with conditions', staff: 'RN Chen' }
      ]
    },
    4: {
      title: 'Waitlist contact log',
      subtitle: 'Scheduled touches while waiting for a bed',
      columns: [
        { key: 'when', label: 'Date' },
        { key: 'channel', label: 'Channel' },
        { key: 'notes', label: 'Notes' }
      ],
      rows: [
        { when: '2025-12-22', channel: 'Phone', notes: 'Placed on waitlist — position confirmed' },
        { when: '2025-12-29', channel: 'Email', notes: 'Fortnightly check-in — still interested' },
        { when: '2026-01-08', channel: 'Phone', notes: 'Bed available discussed — offer prepared' }
      ]
    },
    5: {
      title: 'Offer & agreement history',
      subtitle: 'Quotes, revisions, and signing events',
      columns: [
        { key: 'when', label: 'Date' },
        { key: 'action', label: 'Action' },
        { key: 'detail', label: 'Detail' }
      ],
      rows: [
        { when: '2026-01-05', action: 'Offer issued', detail: `Room offer ${String.fromCharCode(65 + (id % 3))}${100 + id} + quote v1` },
        { when: '2026-01-08', action: 'Agreement pack sent', detail: 'E-sign link + resident agreement' },
        { when: '2026-01-12', action: 'Agreement signed', detail: 'E-signature — deposit schedule triggered' }
      ]
    },
    6: {
      title: 'Admission checklist events',
      subtitle: 'Key tasks with timestamps (excerpt)',
      columns: [
        { key: 'when', label: 'Date' },
        { key: 'task', label: 'Task' },
        { key: 'status', label: 'Status' },
        { key: 'owner', label: 'Owner' }
      ],
      rows: [
        { when: '2026-01-15', task: 'CRM → resident profile conversion', status: 'Done', owner: 'Admissions' },
        { when: '2026-01-15', task: 'NFC / room tag assigned', status: 'Done', owner: 'IT' },
        { when: '2026-01-15', task: 'Medication chart (MAR) prepared', status: 'Done', owner: 'RN' },
        { when: '2026-01-16', task: 'ACER countdown started', status: 'On track', owner: 'Finance' }
      ]
    },
    8: {
      title: 'Exit workflow (no events yet)',
      subtitle: 'Will list tasks when discharge / transfer / exit is initiated',
      columns: [
        { key: 'when', label: 'Date' },
        { key: 'task', label: 'Task' },
        { key: 'status', label: 'Status' }
      ],
      rows: []
    },
    9: {
      title: 'Post-exit follow-up (no events yet)',
      subtitle: 'Surveys and referral nurture after exit',
      columns: [
        { key: 'when', label: 'Date' },
        { key: 'activity', label: 'Activity' },
        { key: 'status', label: 'Status' }
      ],
      rows: []
    }
  };

  if (stage === 7) {
    return getHistoryStage7(subStage7, name, id);
  }

  return tables[stage] || null;
}

function getHistoryStage7(sub, name, id) {
  const common = {
    '7A': {
      title: 'Settling-in reviews & visits',
      subtitle: 'Day 1–28 touchpoints (family, RN, admissions)',
      columns: [
        { key: 'when', label: 'Date' },
        { key: 'type', label: 'Type' },
        { key: 'notes', label: 'Notes' },
        { key: 'owner', label: 'Owner' }
      ],
      rows: [
        { when: '2026-01-15', type: 'Admission day welcome', notes: 'Orientation + meal plan confirmed', owner: 'Care team' },
        { when: '2026-01-16', type: 'RN initial assessments', notes: 'Falls, nutrition, pain — ongoing', owner: 'RN Chen' },
        { when: '2026-01-22', type: 'Family call (Day 7)', notes: 'Portal activation — daughter logged in', owner: 'Admissions' }
      ]
    },
    '7B': {
      title: 'Scheduled assessments & care plan reviews',
      subtitle: 'Recurring clinical and quality touchpoints',
      columns: [
        { key: 'when', label: 'Date' },
        { key: 'assessment', label: 'Assessment / review' },
        { key: 'result', label: 'Result / status' },
        { key: 'assessor', label: 'Assessor' }
      ],
      rows: [
        { when: '2026-02-01', assessment: 'Care plan review (8-weekly)', result: 'Signed — goals updated', assessor: 'RN Chen' },
        { when: '2026-02-10', assessment: 'Falls risk reassessment', result: 'Moderate — interventions unchanged', assessor: 'RN Patel' },
        { when: '2026-03-01', assessment: 'Medication review (RMMR due)', result: 'Scheduled with GP', assessor: 'Pharmacy' },
        { when: '2026-03-15', assessment: 'Monthly care statement', result: 'Issued — viewed on portal', assessor: 'System' }
      ]
    },
    '7C': {
      title: 'Escalation & MDT events',
      subtitle: 'When clinical complexity increases',
      columns: [
        { key: 'when', label: 'Date' },
        { key: 'event', label: 'Event' },
        { key: 'action', label: 'Action taken' }
      ],
      rows: [
        { when: '—', event: 'No active escalation', action: '—' }
      ]
    },
    '7D': {
      title: 'Respite episodes (if applicable)',
      subtitle: 'Episode history and planned discharge',
      columns: [
        { key: 'start', label: 'Start' },
        { key: 'end', label: 'End' },
        { key: 'notes', label: 'Notes' }
      ],
      rows:
        id % 3 === 0
          ? [{ start: '2025-08-01', end: '2025-08-28', notes: 'Previous respite — returned home' }]
          : [{ start: '—', end: '—', notes: 'Permanent admission — no respite episode' }]
    },
    '7E': {
      title: 'Palliative reviews & visits',
      subtitle: 'Family meetings, symptom reviews (when pathway active)',
      columns: [
        { key: 'when', label: 'Date' },
        { key: 'type', label: 'Type' },
        { key: 'notes', label: 'Notes' }
      ],
      rows: [{ when: '—', type: 'Pathway not activated', notes: '—' }]
    }
  };
  return common[sub] || common['7B'];
}

function getStage7SubStageModel(sub, name, id, h) {
  const base = {
    title: 'Stage 7 — Active resident',
    subtitle: 'CRM layer on top of clinical, funding, and compliance (longest stage).',
    status: 'current',
    specRef: 'Compliance.md §7 Active resident',
    subStageTabs: [
      { id: '7A', label: '7A Settling in', short: '0–28 d' },
      { id: '7B', label: '7B Steady state', short: 'Ongoing' },
      { id: '7C', label: '7C Complex / escalated', short: 'As needed' },
      { id: '7D', label: '7D Respite', short: 'If applicable' },
      { id: '7E', label: '7E Palliative', short: 'If applicable' }
    ],
    activeSubStage: sub
  };

  const settling = {
    ...base,
    subStageTitle: '7A — Settling in (days 0–28)',
    subStageDescription: 'Mandatory admission tasks, ACER countdown, family onboarding.',
    sections: [
      {
        title: 'Settling-in checklist (excerpt)',
        fields: [
          { key: 'orientation', label: 'Resident orientation', value: 'Completed Day 1–2', hint: 'Checkbox' },
          { key: 'assessments', label: 'Initial assessments', value: 'In progress — due Day 1–14', hint: 'Progress bar in production' },
          { key: 'care_plan', label: 'Care plan signed off', value: 'Target within 48h of assessments', hint: '' },
          { key: 'acer', label: 'ACER lodgement', value: 'Countdown active — on track', hint: '14 / 7 / 3 day alerts' },
          { key: 'portal', label: 'Family portal activation', value: 'Invites sent Day 1', hint: '' },
          { key: 'day14', label: 'Day 14 satisfaction check', value: 'Scheduled', hint: 'Auto-task' },
          { key: 'day28', label: 'Day 28 settling-in review', value: 'Scheduled — transition to 7B', hint: '' }
        ]
      }
    ]
  };

  const steady = {
    ...base,
    subStageTitle: '7B — Steady state (ongoing)',
    subStageDescription: 'Care plan lifecycle, AN-ACC monitoring, family engagement.',
    sections: [
      {
        title: 'Care plan & reviews (7B.1)',
        fields: [
          { key: 'cp_review', label: 'Care plan review cycle', value: '8-weekly — next due auto-scheduled', hint: 'Minimum 8-weekly' },
          { key: 'summary_refresh', label: 'Summary care plan refresh', value: 'On every care plan save', hint: 'Push to NFC/mobile' },
          { key: 'goals', label: 'Goals of care', value: 'Reviewed with family at last CP review', hint: '' }
        ]
      },
      {
        title: 'AN-ACC & funding (7B.2)',
        fields: [
          { key: 'class', label: 'Current AN-ACC class', value: `Class ${6 + (id % 5)} (demo)`, hint: 'Dashboard in production' },
          { key: 'reassess', label: 'Reassessment eligibility', value: 'Monitored — alerts at 10 / 11 / 12 mo', hint: 'Class-dependent' },
          { key: 'supplements', label: 'Supplement eligibility', value: 'Ongoing monitoring (O₂, enteral, dementia, DVA…)', hint: '' }
        ]
      },
      {
        title: 'Family & portal (7B.4)',
        fields: [
          { key: 'mcs', label: 'Monthly care statement', value: 'Generated 1st of month — portal', hint: '' },
          { key: 'engagement', label: 'Portal engagement', value: h > 0.4 ? 'Active (last 7d)' : 'Reminder due', hint: 'Weekly dashboard' }
        ]
      }
    ]
  };

  const complex = {
    ...base,
    subStageTitle: '7C — Complex / escalated',
    subStageDescription: 'Escalation flags, MDT coordination, heightened CRM touchpoints.',
    sections: [
      {
        title: 'Escalation',
        fields: [
          { key: 'flag', label: 'Escalation flag', value: 'Not active', hint: 'Red banner on all views when active' },
          { key: 'reason', label: 'Reason (if active)', value: '—', hint: '' },
          { key: 'mdt', label: 'MDT review', value: 'As needed', hint: '' }
        ]
      }
    ]
  };

  const respite = {
    ...base,
    subStageTitle: '7D — Respite management',
    subStageDescription: 'Fixed length of stay, billing, and discharge planning.',
    sections: [
      {
        title: 'Respite',
        fields: [
          { key: 'episodes', label: 'Respite episodes', value: id % 3 === 0 ? 'Historical — last ended 2025-08' : 'N/A — permanent admission', hint: '' },
          { key: 'los', label: 'Length of stay (if respite)', value: '—', hint: '' }
        ]
      }
    ]
  };

  const palliative = {
    ...base,
    subStageTitle: '7E — Palliative / end of life',
    subStageDescription: 'Pathway activation, anticipatory meds, family & spiritual support.',
    sections: [
      {
        title: 'Pathway',
        fields: [
          { key: 'pathway', label: 'Palliative pathway', value: 'Not activated', hint: 'RN/GP identification' },
          { key: 'plan', label: 'Palliative care plan', value: '—', hint: 'Linked when active' }
        ]
      }
    ]
  };

  const map = { '7A': settling, '7B': steady, '7C': complex, '7D': respite, '7E': palliative };
  return map[sub] || steady;
}
